import React, { useMemo, useRef, useState } from "react";
import Papa from "papaparse";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Upload, Download, CheckCircle2, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES } from "../lib/perms";

const REQUIRED = ["student_name", "college_name"];
const OPTIONAL = [
  "program_category",
  "course_name",
  "email",
  "phone",
  "intake_name",
  "partner_name",
  "priority",
  "stage",
  "college_app_id",
  "campus",
  "internal_notes",
];

const TEMPLATE = `student_name,email,phone,college_name,program_category,course_name,intake_name,partner_name,priority,stage,college_app_id,campus,internal_notes
Aman Sharma,aman@example.com,+91-99999-11111,Humber College,2 YEAR DIPLOMA,Hospitality Management,September 2026,Maple Edge Consultants,High,New Application,COL-1001,North Campus,Follow up next week
Priya Kaur,priya@example.com,+91-99999-22222,Seneca College,BACHELORS DEGREE,BCom,January 2027,,Medium,Documents Pending,,,Awaiting SOP`;

export default function BulkImportApplicationsDialog({ open, onOpenChange, meta, onImported }) {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [defCollege, setDefCollege] = useState("");
  const [defProgram, setDefProgram] = useState("");
  const [defIntake, setDefIntake] = useState("");
  const [defPartner, setDefPartner] = useState("");
  const [defPriority, setDefPriority] = useState("");
  const [defStage, setDefStage] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setRows([]);
    setFileName("");
    setDefCollege("");
    setDefProgram("");
    setDefIntake("");
    setDefPartner("");
    setDefPriority("");
    setDefStage("");
    setResult(null);
    setImporting(false);
    if (inputRef.current) inputRef.current.value = "";
  };
  const close = () => {
    reset();
    onOpenChange(false);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim().toLowerCase().replace(/\s+/g, "_"),
      complete: (res) => {
        const parsed = (res.data || []).filter((r) => Object.values(r).some((v) => (v || "").toString().trim() !== ""));
        setRows(parsed);
        if (!parsed.length) toast.error("No usable rows found in CSV");
        else toast.success(`Parsed ${parsed.length} rows`);
      },
      error: (err) => toast.error("CSV parse failed: " + err.message),
    });
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "b2b-applications-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const importNow = async () => {
    if (!rows.length) {
      toast.error("Upload a CSV first");
      return;
    }
    const defaults = {};
    if (defCollege) defaults.college_id = defCollege;
    if (defProgram) defaults.program_category = defProgram;
    if (defIntake) defaults.intake_id = defIntake;
    if (defPartner) defaults.partner_id = defPartner;
    if (defPriority) defaults.priority = defPriority;
    if (defStage) defaults.stage = defStage;
    setImporting(true);
    try {
      const r = await api.post("/applications/import", { rows, defaults });
      setResult(r.data);
      if (r.data.imported > 0) toast.success(`Imported ${r.data.imported} application${r.data.imported === 1 ? "" : "s"}`);
      if ((r.data.errors || []).length) toast.message(`${r.data.errors.length} row(s) skipped — see details`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const finish = () => {
    onImported?.();
    close();
  };

  const preview = rows.slice(0, 5);
  const previewCols = ["student_name", "email", "phone", "college_name", "program_category", "course_name", "intake_name", "partner_name", "priority", "stage"];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}>
      <DialogContent data-testid="b2b-import-dialog" className="bg-white max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="heading-font">Bulk Import Applications from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV. Existing students are reused when name and email match. Program category must be one of the five shared categories (left blank → set from the course name or flagged for review). College, intake and partner are matched by name to master data (case-insensitive). Set batch defaults below to fill any rows that miss a value. Rows that can't be matched are reported so you can fix them and re-import.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-3 p-3 border border-dashed border-slate-300 rounded-lg">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Upload className="w-5 h-5 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate" title={fileName}>{fileName || "Choose a .csv file"}</div>
                  <div className="text-xs text-slate-500">
                    Required: <span className="mono">{REQUIRED.join(", ")}</span> · Optional: <span className="mono">{OPTIONAL.join(", ")}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input ref={inputRef} type="file" accept=".csv" onChange={onFile} className="hidden" id="b2b-csv-input" data-testid="b2b-csv-input" />
                <label htmlFor="b2b-csv-input" className="inline-flex items-center px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm cursor-pointer hover:bg-slate-800">
                  Choose CSV
                </label>
                <Button data-testid="b2b-csv-template" variant="outline" size="sm" onClick={downloadTemplate}>
                  <Download className="w-4 h-4 mr-1" />
                  Template
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Default College (fills empty rows)</Label>
                <Select value={defCollege} onValueChange={setDefCollege}>
                  <SelectTrigger data-testid="b2b-default-college"><SelectValue placeholder="Leave empty if CSV has it" /></SelectTrigger>
                  <SelectContent>
                    {(meta?.colleges || []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.code ? `${c.code} — ${c.name}` : c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Default Program Category</Label>
                <Select value={defProgram} onValueChange={setDefProgram}>
                  <SelectTrigger data-testid="b2b-default-category"><SelectValue placeholder="Leave empty if CSV has it" /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Default Intake</Label>
                <Select value={defIntake} onValueChange={setDefIntake}>
                  <SelectTrigger data-testid="b2b-default-intake"><SelectValue placeholder="Leave empty if CSV has it" /></SelectTrigger>
                  <SelectContent>
                    {(meta?.intakes || []).map((i) => (
                      <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Default Partner</Label>
                <Select value={defPartner} onValueChange={setDefPartner}>
                  <SelectTrigger data-testid="b2b-default-partner"><SelectValue placeholder="No partner" /></SelectTrigger>
                  <SelectContent>
                    {(meta?.partners || []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Default Priority</Label>
                <Select value={defPriority} onValueChange={setDefPriority}>
                  <SelectTrigger data-testid="b2b-default-priority"><SelectValue placeholder="Medium" /></SelectTrigger>
                  <SelectContent>
                    {["Low", "Medium", "High", "Urgent"].map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Default Stage</Label>
                <Select value={defStage} onValueChange={setDefStage}>
                  <SelectTrigger data-testid="b2b-default-stage"><SelectValue placeholder="New Application" /></SelectTrigger>
                  <SelectContent>
                    {(meta?.stages || ["New Application", "Documents Pending"]).map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {rows.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-3 py-2 text-xs text-slate-600 flex items-center justify-between">
                  <span><strong>{rows.length}</strong> rows parsed · showing first {preview.length}</span>
                  <button onClick={() => { setRows([]); setFileName(""); if (inputRef.current) inputRef.current.value = ""; }} className="text-rose-600 hover:underline text-xs flex items-center">
                    <X className="w-3 h-3 mr-0.5" />Clear
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 text-slate-500 uppercase">
                      <tr>{previewCols.map((h) => (<th key={h} className="px-2 py-1 text-left whitespace-nowrap">{h}</th>))}</tr>
                    </thead>
                    <tbody>
                      {preview.map((r, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          {previewCols.map((h) => (
                            <td key={h} className="px-2 py-1 whitespace-nowrap">
                              {r[h] || <span className="text-slate-300">—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              <div>
                <div className="font-semibold text-emerald-900">Imported {result.imported} application{result.imported === 1 ? "" : "s"}</div>
                <div className="text-xs text-emerald-800">{(result.errors || []).length} error{(result.errors || []).length === 1 ? "" : "s"} · {(result.skipped_duplicates || []).length} duplicate row(s) skipped</div>
              </div>
            </div>
            {(result.skipped_duplicates || []).length > 0 && (
              <div data-testid="import-skipped-duplicates" className="border border-slate-200 rounded-lg bg-slate-50 p-3 text-xs text-slate-700 max-h-40 overflow-y-auto">
                <div className="font-semibold mb-1">Skipped — already exists (existing application unchanged)</div>
                {result.skipped_duplicates.map((s, i) => <div key={i}>Row {s.row} · {s.student_name} → matches <span className="mono">{s.matched_application_id}</span></div>)}
              </div>
            )}
            {(result.errors || []).length > 0 && (
              <div className="border border-amber-200 rounded-lg bg-amber-50 p-3">
                <div className="flex items-center gap-2 text-amber-900 font-medium text-sm mb-2">
                  <AlertTriangle className="w-4 h-4" />Rows that could not be imported
                </div>
                <div className="max-h-48 overflow-y-auto text-xs space-y-1">
                  {result.errors.map((e, i) => (
                    <div key={i}>Row {e.row}: <span className="text-amber-900">{e.reason}</span></div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button data-testid="b2b-import-confirm" onClick={importNow} disabled={!rows.length || importing} className="bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50">
                {importing ? "Importing…" : `Import ${rows.length || ""} applications`}
              </Button>
            </>
          ) : (
            <Button data-testid="b2b-import-done" onClick={finish} className="bg-slate-900 hover:bg-slate-800 text-white">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
